// Brand page inventory: CaboVax is served by this route file (`_public.cabovax.tsx`).
import type { LinksFunction, MetaFunction } from "react-router";
import CaboVaxLogo from "~/assets/logos/new_logos/CaboVax@4x_Claim.png";
import ContactBannerImage from "~/assets/images/cabopoc_contact_banner.jpeg";
import { ContactBanner } from "~/features/microsite/ContactBanner";
import { PublicFooter } from "~/features/public/Footer";
import { NavBar } from "~/features/public/NavBar";

export const meta: MetaFunction = () => {
  return [
    { title: "CaboVax – Impfen in der Apotheke, endlich einfach." },
    {
      name: "description",
      content:
        "CaboVax ist die erste vollständig integrierte Impfplattform für Apotheken – von Terminbuchung über Anamnese bis Abrechnung. ApoStart Award Nominierung.",
    },
    {
      name: "keywords",
      content: "CaboVax, Impfen, Apotheke, Impfplattform, Terminbuchung, KI-Anamnese, ApoStart Award, DEMIS, Hamburg",
    },
    { name: "author", content: "HEALYZER – Health Analyzing Technologies GmbH" },
    { name: "robots", content: "index, follow" },
    { httpEquiv: "Content-Language", content: "de" },

    // Open Graph / Facebook
    { property: "og:type", content: "website" },
    { property: "og:title", content: "CaboVax – Impfen in der Apotheke, endlich einfach." },
    {
      property: "og:description",
      content:
        "CaboVax ist die erste vollständig integrierte Impfplattform für Apotheken – von Terminbuchung über Anamnese bis Abrechnung. ApoStart Award Nominierung.",
    },
    { property: "og:url", content: "https://www.cabocheck.com/cabovax" },
    { property: "og:site_name", content: "CaboVax" },
    { property: "og:locale", content: "de_DE" },

    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "CaboVax – Impfen in der Apotheke, endlich einfach." },
    {
      name: "twitter:description",
      content:
        "CaboVax ist die erste vollständig integrierte Impfplattform für Apotheken – von Terminbuchung über Anamnese bis Abrechnung.",
    },

    // Additional SEO
    { name: "theme-color", content: "#4f46e5" },
    { name: "format-detection", content: "telephone=no" },
    { rel: "canonical", href: "https://www.cabocheck.com/cabovax" },
  ];
};

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap",
  },
  { rel: "icon", href: "/cabovax-apple-touch-icon.png", type: "image/png" },
  { rel: "apple-touch-icon", href: "/cabovax-apple-touch-icon.png", sizes: "180x180" },
  { rel: "shortcut icon", href: "/cabovax-favicon.ico" },
];

// Constants
const VOTING_URL = "https://www.pharmazeutische-zeitung.de/wer-bekommt-die-wild-card-fuers-apostart-finale-157882/";

const COMPANY_INFO = {
  name: "CaboVax",
  fullName: "HEALYZER – Health Analyzing Technologies GmbH",
  address: {
    street: "Zimmerpforte 3",
    postal: "20099",
    city: "Hamburg",
    country: "DE",
  },
  email: "hilfe@cabocheck.de",
  url: "https://www.cabocheck.com/cabovax",
};

const navigationItems = [
  { href: "#features", label: "Funktionen" },
  { href: "#benefits", label: "Vorteile" },
  { href: "#how", label: "So funktioniert's" },
  { href: "#contact", label: "Kontakt aufnehmen" },
];

export default function CaboVax() {
  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const element = document.getElementById(targetId.replace("#", ""));
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Reusable logo component
  const CompanyLogo = ({ className = "w-64" }: { className?: string }) => (
    <div className="flex items-center gap-2">
      <img src={CaboVaxLogo} alt="CaboVax Logo" className={`${className} select-none`} />
      {/* <span className="font-semibold">{COMPANY_INFO.name}</span> */}
    </div>
  );

  // JSON-LD structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: COMPANY_INFO.name,
    description:
      "Die erste vollständig integrierte Impfplattform für Apotheken – von Terminbuchung über Anamnese bis Abrechnung.",
    url: COMPANY_INFO.url,
    logo: `${COMPANY_INFO.url}-logo.png`,
    foundingLocation: {
      "@type": "Place",
      name: COMPANY_INFO.address.city,
      addressCountry: COMPANY_INFO.address.country,
    },
    parentOrganization: {
      "@type": "Organization",
      name: COMPANY_INFO.fullName,
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY_INFO.address.street,
      postalCode: COMPANY_INFO.address.postal,
      addressLocality: COMPANY_INFO.address.city,
      addressCountry: COMPANY_INFO.address.country,
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: COMPANY_INFO.email,
      contactType: "customer service",
    },
    sameAs: [VOTING_URL],
  };

  return (
    <>
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />

      <div className="scroll-smooth bg-slate-50 font-inter text-slate-900" lang="de">
        <div className="relative bg-public-radial">
          <div className="absolute inset-x-0 -top-20 -z-10 h-52 bg-gradient-to-b from-indigo-200/40 to-transparent"></div>
          <NavBar
            logo={<CompanyLogo className="h-20 w-auto" />}
            logoTo="/"
            navItems={[
              { to: "/", label: "← Startseite", id: "home" },
              ...navigationItems.map((item) => ({
                to: `/cabovax${item.href}`,
                label: item.label,
                id: item.href.replace("#", ""),
              })),
            ]}
          />
          <section className="public-container pb-14 pt-4">
            <div className="grid gap-10 md:grid-cols-2 md:items-start">
              <div>
                <h1 className="sr-only">
                  Impfen in der Apotheke, <span className="text-indigo-700">endlich einfach</span>.
                </h1>
                <p className="mt-10 max-w-xl text-lg text-slate-700">
                  CaboVax ist die erste vollständig integrierte Impfplattform speziell für Apotheken. Von der
                  Terminbuchung über die KI‑gestützte Anamnese bis zur rechtssicheren Dokumentation und Abrechnung –
                  alles in <span className="font-semibold">einer</span> Anwendung.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="#features"
                    onClick={(e) => handleSmoothScroll(e, "#features")}
                    className="rounded-xl border border-slate-300 bg-white px-5 py-3 font-semibold hover:border-slate-400"
                  >
                    Mehr erfahren
                  </a>
                </div>
              </div>

              {/* IMAGE 1: Inline SVG Illustration */}
              <div className="relative isolate">
                <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-indigo-100 to-blue-100"></div>
                <svg
                  viewBox="0 0 640 420"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-auto w-full drop-shadow"
                  role="img"
                  aria-label="Illustration showing vaccination safety with shield, vaccine vial, syringe and checkmarks"
                >
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="currentColor" className="text-indigo-500" />
                      <stop offset="100%" stopColor="currentColor" className="text-blue-400" />
                    </linearGradient>
                  </defs>
                  {/* Shield */}
                  <path
                    d="M320 60c60 30 120 20 160 10 0 150-60 240-160 280-100-40-160-130-160-280 40 10 100 20 160-10z"
                    fill="url(#g1)"
                    opacity="0.15"
                  />
                  {/* Vaccine vial */}
                  <rect
                    x="210"
                    y="120"
                    width="110"
                    height="170"
                    rx="16"
                    className="fill-white stroke-slate-400"
                    strokeWidth="3"
                  />
                  <rect x="225" y="140" width="80" height="20" rx="6" className="fill-indigo-200" />
                  <rect x="225" y="180" width="80" height="80" rx="8" className="fill-slate-200" />
                  <rect x="240" y="190" width="50" height="10" rx="5" className="fill-slate-400" />
                  <rect x="240" y="210" width="50" height="10" rx="5" className="fill-slate-400" />
                  {/* Syringe */}
                  <rect x="360" y="160" width="180" height="16" rx="8" className="fill-slate-400" />
                  <rect x="360" y="176" width="140" height="10" rx="5" className="fill-slate-300" />
                  <rect x="540" y="165" width="28" height="22" rx="6" className="fill-blue-400" />
                  <rect x="568" y="167" width="4" height="18" rx="2" className="fill-sky-500" />
                  {/* Check marks */}
                  <circle cx="190" cy="330" r="6" className="fill-green-500" />
                  <circle cx="220" cy="330" r="6" className="fill-green-500" />
                  <circle cx="250" cy="330" r="6" className="fill-green-500" />
                </svg>
              </div>
            </div>
          </section>
        </div>

        {/* Logos / Trust */}
        <section className="public-container pb-6">
          <div className="flex flex-wrap items-center gap-x-10 gap-y-4 text-slate-500">
            <span className="text-xs uppercase tracking-wider">Konform mit STIKO / BMG / Berufsverbänden</span>
            <span aria-hidden="true" className="h-4 w-px bg-slate-300"></span>
            <span className="text-xs uppercase tracking-wider">Vorbereitet auf DEMIS</span>
            <span aria-hidden="true" className="h-4 w-px bg-slate-300"></span>
            <span className="text-xs uppercase tracking-wider">Made in Hamburg</span>
          </div>
        </section>

        {/* Vorteile */}
        <section id="benefits" className="public-container py-12">
          <div className="grid gap-8 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold">Alles in einer Anwendung</h3>
              <p className="mt-2 text-sm text-slate-600">
                Keine Insellösungen: Termin, Anamnese, Einwilligung, Dokumentation & Abrechnung nahtlos verbunden.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold">Weniger Aufwand & mehr Sicherheit</h3>
              <p className="mt-2 text-sm text-slate-600">
                Standardisierte, rechtssichere Abläufe – jede Impfung in unter acht Minuten.
              </p>
            </div>
            {/* <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold">Impf‑KI für den Alltag</h3>
              <p className="mt-2 text-sm text-slate-600">
                Kontraindikationen, Aufklärung, Vorgaben – Antworten direkt in der Anwendung.
              </p>
            </div> */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold">Schneller Start</h3>
              <p className="mt-2 text-sm text-slate-600">
                Mitgelieferte Schulungen, SOPs und Rollen/Rechte erleichtern den Einstieg.
              </p>
            </div>
          </div>
        </section>

        {/* Funktionen */}
        <section id="features" className="public-container py-12">
          <div className="mb-8 max-w-2xl">
            <h2 className="text-2xl font-bold">Was CaboVax leistet</h2>
            <p className="mt-2 text-slate-700">
              Die zentrale Plattform für moderne, effiziente und rechtssichere Impfprozesse in Apotheken.
            </p>
          </div>

          <div className="grid items-start gap-12 md:grid-cols-2">
            <ul className="space-y-4">
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-indigo-600"></span>
                <p>
                  <span className="font-semibold">Digitale Terminvergabe</span> mit Erinnerungen und QR Option.
                </p>
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-indigo-600"></span>
                <p>
                  <span className="font-semibold">Digitale Einwilligung</span> mit Zeitstempel & Unterschrift des
                  Kunden.
                </p>
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-indigo-600"></span>
                <p>
                  <span className="font-semibold">Automatisierte Dokumentation</span> von allen notwendigen
                  Abrechnungsdaten
                </p>
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-indigo-600"></span>
                <p>
                  <span className="font-semibold">Schnelle Abwicklung</span> des Impfvorgangs für Impfkampagnen
                </p>
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-indigo-600"></span>
                <p>
                  <span className="font-semibold">Abrechnung</span> – abrechnungsfähige Daten für Ihr POS-System
                </p>
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-indigo-600"></span>
                <p>
                  <span className="font-semibold">DEMIS Vorbereitung</span> für die gesetzlich vorgesehene
                  Impfdatenerfassung.
                </p>
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-indigo-600"></span>
                <p>
                  <span className="font-semibold">Analytics & Monitoring</span> – Impfzahlen, Abrechnungsstatus u.v.m.
                </p>
              </li>
              <li className="flex gap-3">
                <span aria-hidden="true" className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-indigo-600"></span>
                <p>
                  <span className="font-semibold">Rollen & Rechte</span> – klare Aufgaben und Freigaben für das Team.
                </p>
              </li>
            </ul>

            {/* IMAGE 2: Simple dashboard mockup (SVG) */}
            <div className="relative isolate p-6">
              <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-blue-100 to-indigo-100"></div>
              <svg
                viewBox="0 0 900 560"
                xmlns="http://www.w3.org/2000/svg"
                className="h-auto w-full drop-shadow"
                role="img"
                aria-label="CaboVax dashboard interface mockup showing charts, statistics and data panels"
              >
                <rect x="10" y="10" width="880" height="540" rx="16" className="fill-white stroke-slate-200" />
                <rect x="30" y="30" width="200" height="18" rx="4" className="fill-indigo-200" />
                <rect x="30" y="60" width="140" height="10" rx="4" className="fill-slate-200" />
                <rect x="30" y="80" width="180" height="10" rx="4" className="fill-slate-200" />
                <rect x="30" y="100" width="160" height="10" rx="4" className="fill-slate-200" />
                <rect x="250" y="60" width="620" height="200" rx="10" className="fill-slate-100" />
                <g>
                  <rect x="280" y="110" width="30" height="120" className="fill-indigo-500" />
                  <rect x="330" y="150" width="30" height="80" className="fill-blue-400" />
                  <rect x="380" y="70" width="30" height="160" className="fill-indigo-500" />
                  <rect x="430" y="130" width="30" height="100" className="fill-blue-400" />
                  <rect x="480" y="90" width="30" height="140" className="fill-indigo-500" />
                  <rect x="530" y="170" width="30" height="60" className="fill-blue-400" />
                  <rect x="580" y="120" width="30" height="110" className="fill-indigo-500" />
                  <rect x="630" y="140" width="30" height="90" className="fill-blue-400" />
                </g>
                <rect x="250" y="280" width="190" height="90" rx="10" className="fill-white stroke-slate-200" />
                <rect x="460" y="280" width="190" height="90" rx="10" className="fill-white stroke-slate-200" />
                <rect x="670" y="280" width="190" height="90" rx="10" className="fill-white stroke-slate-200" />
                <rect x="250" y="390" width="610" height="140" rx="10" className="fill-white stroke-slate-200" />
                <rect x="710" y="30" width="160" height="26" rx="13" className="fill-indigo-100" />
                <rect x="530" y="30" width="160" height="26" rx="13" className="fill-blue-100" />
              </svg>
            </div>
          </div>
        </section>

        {/* So funktioniert's */}
        <section id="how" className="public-container py-12">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-bold">So funktioniert&apos;s in der Praxis</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 p-6">
                <div className="text-sm font-semibold text-indigo-700">1 · Termin & Anamnese</div>
                <p className="mt-2 text-sm text-slate-700">
                  Patient bucht online oder vor Ort, füllt die KI‑gestützte Anamnese aus; Einwilligung digital.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 p-6">
                <div className="text-sm font-semibold text-indigo-700">2 · Impfung & Doku</div>
                <p className="mt-2 text-sm text-slate-700">
                  Durchführung in der Offizin; Charge, Datum, Stempel & Aufklärung werden automatisch dokumentiert.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 p-6">
                <div className="text-sm font-semibold text-indigo-700">3 · Abrechnung & Reporting</div>
                <p className="mt-2 text-sm text-slate-700">
                  Abrechnungsfähige Daten für das DAV‑Portal; Dashboard mit Impfzahlen & Status.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="public-container py-12">
          <ContactBanner
            eyebrow="CaboVax kennenlernen"
            title="Kontaktieren Sie uns noch heute"
            description="Registrieren Sie sich und wir melden uns bei Ihnen, um CaboVax, Ihre Impfprozesse und den Start in Ihrer Apotheke gemeinsam zu planen."
            to="/register/pharmacy/step2"
            buttonLabel="Kontakt aufnehmen"
            imageSrc={ContactBannerImage}
            imageAlt="Apothekenteam im Beratungsgespräch"
            eyebrowClassName="text-indigo-200"
          />
        </section>

        <PublicFooter />
      </div>
    </>
  );
}
